/**
 * Billing-contact sessions (site-trial plan amendment D48).
 *
 * A billing contact may hold NO role in the organisation. `GET /v5/organisations/orgByPerson` marks that org entry
 * `billingContactOnly: true`, and the App Server then admits the person ONLY to the billing routes (everything else is
 * 403). The shell must therefore load nothing operational for such an org and land on the Billing page.
 *
 * 🛑 THE MARKER IS A HINT ABOUT WHAT TO LOAD, NEVER AN AUTHORISATION. The server decides what this person may read; the
 * client only avoids asking for things it will be refused. Do not use it to grant anything.
 */

export const BILLING_PATH = '/access/billing';

/** The two Stripe return markers the server builds into its fixed return URLs. */
export const SETUP_RETURN = Object.freeze({ RETURNED: 'return', CANCELLED: 'cancelled' });

/** An org entry from orgByPerson that admits this person to billing only. */
export const isBillingOnlyWorkspace = (workspace) => workspace?.billingContactOnly === true;

/** The selected profile is a billing-only session for its org. */
export const isBillingOnlyProfile = (profile) => profile?.billingContactOnly === true;

/** Redux selector form, for useSelector and sagas. */
export const selectIsBillingOnlyProfile = (state) => isBillingOnlyProfile(state?.Auth?.selectedProfile);

/**
 * The profile a billing-only session runs on. It is built from the bootstrap entry alone: the person read
 * (`GET /people/:id`) is an operational route the server refuses to this person, so it is never made.
 */
export const billingOnlyProfileFor = (workspace) => ({
	personID: workspace.personID ?? null,
	orgID: workspace.orgID,
	roles: {},
	billingContactOnly: true,
	orgAlias: workspace.alias,
	orgSlug: workspace.orgSlug || null
});

const ORG_ID_PATTERN = /^[1-9]\d{0,9}$/;

/** `?orgID=` on a Billing URL, or null. Only a positive integer is ever accepted. */
export const parseBillingOrgID = (search) => {
	try {
		const raw = new URLSearchParams(search || '').get('orgID');
		return ORG_ID_PATTERN.test(raw ?? '') ? Number(raw) : null;
	} catch {
		return null;
	}
};

/** `?setup=` on a Billing URL: 'return', 'cancelled' or null. Anything else is ignored. */
export const parseSetupMarker = (search) => {
	try {
		const raw = new URLSearchParams(search || '').get('setup');
		return Object.values(SETUP_RETURN).includes(raw) ? raw : null;
	} catch {
		return null;
	}
};

/**
 * The one Billing URL shape this app ever navigates to. Built, never copied from input: the return path after sign-in
 * is fixed (audit C1, allowlisted return paths), so nothing a link carries can redirect anywhere else.
 */
export const billingPathFor = (orgID, setup = null) => {
	const params = new URLSearchParams();
	if (orgID != null && ORG_ID_PATTERN.test(String(orgID))) params.set('orgID', String(orgID));
	if (setup && Object.values(SETUP_RETURN).includes(setup)) params.set('setup', setup);
	const qs = params.toString();
	return qs ? `${BILLING_PATH}?${qs}` : BILLING_PATH;
};
